"use client";

import { useMemo, useState } from "react";
import {
  ErrorNotice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  EXPRESSION_ERROR_STRINGS,
  expressionErrorMessage,
} from "@/components/tools/calculus/expression-errors";
import {
  type Constant,
  readConstant,
} from "@/components/tools/calculus/read-constant";
import { TaylorResults } from "@/components/tools/calculus/taylor-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type Node,
  substitute,
  variable as variableNode,
} from "@/lib/tool-kit/calculus/ast";
import { type ExprError, parseExpression } from "@/lib/tool-kit/calculus/parse";
import {
  MAX_TAYLOR_ORDER,
  type TaylorResult,
  taylorSeries,
} from "@/lib/tool-kit/calculus/taylor";

/** English strings. A translated content file overrides these through `ui`. */
export const TAYLOR_SERIES_CALCULATOR_STRINGS = {
  functionLabel: "Function f(x)",
  functionHint:
    "Use + − * / ^ and brackets. Functions: sin, cos, tan, sec, csc, cot, asin, acos, atan, sinh, cosh, tanh, exp, ln, log (base 10), sqrt, abs. Constants: pi and e.",
  centreLabel: "Expand about x = a, where a =",
  centreHint:
    "0 gives a Maclaurin series. A number or constant such as 1 or pi/4.",
  orderLabel: "Degree n of the polynomial",
  pointLabel: "Test the approximation at x =",
  pointHint: "Compares Tₙ(x) with f(x) and with the error bound.",
  widthLabel: "Graph half-width around a",
  results: "Results",
  maclaurinHeading: "Maclaurin polynomial of degree {n}",
  taylorHeading: "Taylor polynomial of degree {n} about x = {centre}",
  formulaLine: "Tₙ(x) = Σ f⁽ᵏ⁾(a)/k! · (x − a)ᵏ, for k = 0 to n",
  copyPolynomial: "Copy polynomial",
  copyLatex: "Copy LaTeX",
  copied: "Copied!",
  tableHeading: "Coefficients",
  columnOrder: "k",
  columnDerivative: "f⁽ᵏ⁾(x)",
  columnAtCentre: "f⁽ᵏ⁾(a)",
  columnCoefficient: "f⁽ᵏ⁾(a) / k!",
  approxHeading: "Approximation at x = {point}",
  approxPolynomial: "Tₙ(x)",
  approxActual: "f(x)",
  approxError: "Actual error |f(x) − Tₙ(x)|",
  approxBound: "Lagrange error bound",
  approxBoundNote:
    "Lagrange's bound is M·|x − a|ⁿ⁺¹/(n + 1)! with M ≈ {max}, the largest |f⁽ⁿ⁺¹⁾| found by sampling between a and x, so it is an estimate rather than a proof.",
  approxFar:
    "x is far from a, so the polynomial may be a poor approximation here: a Taylor series only converges inside its radius of convergence.",
  undefinedHere: "undefined",
  graphHeading: "Graph",
  graphTitle:
    "Graph of f and its degree {n} Taylor polynomial about x = {centre}",
  seriesFunction: "f(x)",
  seriesPolynomial: "Tₙ(x), n = {n}",
  errorHeading: "Approximation error",
  errorTitle:
    "Graph of the error |f(x) − Tₙ(x)|, which is 0 at x = {centre} and grows away from it",
  seriesError: "|f(x) − Tₙ(x)|",
  pointLabelText: "({x}, {y})",
  downloadPng: "Download graph (PNG)",
  downloadSvg: "Download graph (SVG)",
  centreInvalid:
    "Enter the centre as a number or a constant expression such as 0, 1 or pi/4.",
  pointInvalid:
    "Enter the test point as a number or a constant expression such as 1 or pi/4.",
  oneVariable:
    "Use a function of one variable, such as sin(x). This tool expands y = f(x).",
  undefinedAtCentre:
    "The series cannot be built: f⁽ᵏ⁾(a) is undefined for k = {order} at x = {centre}. Pick a centre where f and its derivatives exist (for ln x, try 1). A removable gap such as sin(x)/x at 0 is not handled: expand sin(x) and divide the result by x.",
  truncated:
    "Only terms up to degree {n} are shown: the next derivative is too large to compute here.",
  expressionErrors: EXPRESSION_ERROR_STRINGS,
} as const;

export type TaylorStrings = typeof TAYLOR_SERIES_CALCULATOR_STRINGS;

export type TaylorView =
  | { kind: "parse-error"; error: ExprError }
  | { kind: "many-variables" }
  | { kind: "bad-centre" }
  | {
      kind: "ok";
      ast: Node;
      centre: Constant;
      decimals: boolean;
      order: number;
      result: TaylorResult;
    };

const ORDERS = Array.from({ length: MAX_TAYLOR_ORDER }, (_, i) =>
  String(i + 1)
);

function buildView(
  text: string,
  centreText: string,
  order: number
): TaylorView {
  const parsed = parseExpression(text);
  if (!parsed.ok) {
    return { kind: "parse-error", error: parsed.error };
  }
  if (parsed.variables.length > 1) {
    return { kind: "many-variables" };
  }
  const original = parsed.variables[0] ?? "x";
  const ast =
    original === "x"
      ? parsed.ast
      : substitute(parsed.ast, original, variableNode("x"));
  const centre = readConstant(centreText);
  if (centre === null || centre === "invalid") {
    return { kind: "bad-centre" };
  }
  return {
    kind: "ok",
    ast,
    centre,
    decimals: parsed.usesDecimals,
    order,
    result: taylorSeries(ast, "x", centre.ast, centre.value, order),
  };
}

function problemMessage(
  view: TaylorView,
  strings: TaylorStrings
): string | null {
  switch (view.kind) {
    case "parse-error":
      return expressionErrorMessage(view.error, strings.expressionErrors);
    case "many-variables":
      return strings.oneVariable;
    case "bad-centre":
      return strings.centreInvalid;
    default:
      return null;
  }
}

export default function TaylorSeriesCalculatorWidget() {
  const strings = useToolUi(TAYLOR_SERIES_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("sin(x)");
  const [centreText, setCentreText] = useState("0");
  const [orderText, setOrderText] = useState("5");
  const [pointText, setPointText] = useState("1");
  const [widthText, setWidthText] = useState("4");
  const view = useMemo(
    () => buildView(text, centreText, Number(orderText)),
    [text, centreText, orderText]
  );
  const halfWidth = Math.max(parseNumberInput(widthText) ?? 4, 1e-6);
  const message = problemMessage(view, strings);
  const point = readConstant(pointText);
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <TextField
            hint={strings.functionHint}
            id="ts-function"
            label={strings.functionLabel}
            mono
            onChange={setText}
            value={text}
          />
        </div>
        <TextField
          hint={strings.centreHint}
          id="ts-centre"
          label={strings.centreLabel}
          mono
          onChange={setCentreText}
          value={centreText}
        />
        <SelectField
          id="ts-order"
          label={strings.orderLabel}
          onChange={setOrderText}
          options={ORDERS.map((value) => ({ value, label: value }))}
          value={orderText}
        />
        <TextField
          hint={strings.pointHint}
          id="ts-point"
          label={strings.pointLabel}
          mono
          onChange={setPointText}
          value={pointText}
        />
        <TextField
          id="ts-width"
          label={strings.widthLabel}
          mono
          numeric
          onChange={setWidthText}
          value={widthText}
        />
      </div>
      <section aria-live="polite" className="space-y-5">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {message ? <ErrorNotice>{message}</ErrorNotice> : null}
        {point === "invalid" ? (
          <ErrorNotice>{strings.pointInvalid}</ErrorNotice>
        ) : null}
        {view.kind === "ok" ? (
          <TaylorResults
            fmt={fmt}
            halfWidth={halfWidth}
            point={point === "invalid" ? null : point}
            strings={strings}
            view={view}
          />
        ) : null}
      </section>
    </>
  );
}
